import { ReactElement } from 'react'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { client } from '@/libs/apollo'
import { IGetServerSidePropsData } from './slug.types'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import Image from 'next/image'
import placeholderImage from '../../../../public/assets/card/card-placeholder-light.svg'

import Head from '@/components/head/head.component'
import PostLayout from '@/layouts/post/post.layout'
import ShareButtons from '@/components/share-buttons/share-buttons.component'

import { postQuery } from '@/api/post/post.queries'
import {
  CATEGORIES,
  ICategoriesQueryData,
} from '@/graphql/category/category.queries'
import {
  IPostsQuery,
  IPostsQueryVars,
  POSTS_QUERY,
} from '@/graphql/post/post.queries'

export const getServerSideProps: GetServerSideProps<IGetServerSidePropsData> =
  async (context) => {
    if (typeof context.params?.slug !== 'string') return { notFound: true }
    const data = await postQuery(context.params.slug)

    if (!data) return { redirect: { destination: '/', permanent: false } }
    // if (data.isPublic === false)
    //   return { redirect: { destination: data.url, permanent: false } }

    const categoriesData = await client.query<ICategoriesQueryData>({
      query: CATEGORIES,
    })

    const { data: postsData } = await client.query<
      IPostsQuery,
      IPostsQueryVars
    >({
      query: POSTS_QUERY,
      variables: {
        query: {
          limit: 5,
          offset: 0,
        },
      },
    })

    const { data: patreonPostsData } = await client.query<
      IPostsQuery,
      IPostsQueryVars
    >({
      query: POSTS_QUERY,
      variables: {
        query: {
          limit: 10,
          offset: 0,
          isPublic: false,
        },
      },
    })

    return {
      props: {
        post: data,
        categories: categoriesData.data.categories.items,
        patreonPosts: patreonPostsData.posts.items,
        latestPosts: postsData.posts.items,
      },
    }
  }

export default function BlogPage({
  post,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter()

  if (post?.isPublic === false) {
    setTimeout(() => {
      router.push(post?.url ?? '/')
    }, 1000)

    return (
      <>
        <Head
          title={post?.meta?.title}
          description={post?.meta?.description}
          image={post?.meta?.image}
        ></Head>

        <Wrapper>
          <div />
          {/* <ShareButtons
            url={`https://ivetars.sk/blog/${post?.slug}`}
            image={post?.meta?.image}
          /> */}
          <div className="container">
            <Title>{post?.title ?? ''}</Title>
            <Container>
              <Image
                src={post?.meta?.image ?? placeholderImage}
                alt={post?.meta?.title ?? ''}
              />

              <p>Tento článok čítajú Patróni.</p>
              <p>O chvíľu budeš presmerovaný na Patreon..</p>
              <p>
                Nič sa nedeje? Klikni <a href={post.url}>TUTO.</a>
              </p>
            </Container>
          </div>
        </Wrapper>
      </>
    )
  }

  return (
    <>
      <Head
        title={post?.meta?.title}
        description={post?.meta?.description}
        image={post?.meta?.image}
      ></Head>

      <Wrapper>
        <ShareButtons
          url={`https://ivetars.sk/blog/${post?.slug}`}
          image={post?.meta?.image}
        />
        <div className="container">
          <Title>{post?.title ?? ''}</Title>
          <Container
            dangerouslySetInnerHTML={{ __html: post?.content ?? '' }}
          />
        </div>
      </Wrapper>
    </>
  )
}

BlogPage.getLayout = function getLayout(page: ReactElement) {
  const props: IGetServerSidePropsData = page.props

  return (
    <PostLayout
      categories={props.categories ?? []}
      latestPosts={props.latestPosts ?? []}
      patreonPosts={props.patreonPosts ?? []}
    >
      {page}
    </PostLayout>
  )
}

const Title = styled.h1`
  font-size: 3rem;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 5rem;
  line-height: 1.25;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 6.2rem;
  }
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 5rem 1fr;
`

const Container = styled.div`
  max-width: 62rem;
  margin: 0 auto;
  img {
    width: 100%;
    max-height: 30rem;
    margin-bottom: 2rem;
    object-fit: contain;
  }

  blockquote {
    font-size: 1.6rem;
    border-left: 0.5rem solid ${({ theme }) => theme.fonts.secondary};
    padding-left: 2rem;

    &:not(:last-child) {
      padding-bottom: 1rem;
    }

    &:last-of-type {
      margin-bottom: 4rem;
    }
  }

  p {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
    font-weight: 300;
  }

  h1 {
    font-size: 3.2rem;
    line-height: 1.2;
    margin-bottom: 2rem;
  }

  h2 {
    font-size: 2.8rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  h3 {
    font-size: 2.4rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  h4 {
    font-size: 2rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  h5 {
    font-size: 1.6rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  h6 {
    font-size: 1.2rem;
    line-height: 1.2;

    margin-bottom: 2rem;
  }

  ul {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
  }

  ol {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
  }

  li {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
  }

  a {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 2rem;
    color: ${({ theme }) => theme.fonts.secondary};
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }
`
